/**
 * Height, weight, BSA and heart rate are report measurements, not Study
 * columns, so they no longer arrive in `report.context`. The Context section
 * still renders them, so they are bridged here by canonical key, and the
 * derived engine sees them as rows of a synthetic group built from the
 * canonicals (formula, precision, unit) so edits to them cascade like any
 * worksheet row.
 */

import type { Context, Measurement } from '@/models/report';
import type {
  CanonicalMeasurement,
  MeasurementGroup,
  MeasurementLabel,
} from '@/models/template';

/** Context class → the canonical key holding the value. */
export const MEASUREMENT_CONTEXT_KEYS: Record<string, string> = {
  HEIGHT: 'HT',
  WEIGHT: 'WT',
  BODY_SURFACE_AREA: 'BSA',
  HEART_RATE: 'HR',
};

/** The same keys, as the formula engine takes them. */
export const CONTEXT_OPERAND_KEYS = Object.values(MEASUREMENT_CONTEXT_KEYS);

/** Keys the reader may type into from the Context section. */
const EDITABLE_KEYS = new Set(['HT', 'WT', 'BSA']);

/** Canonical keys whose row shows a unit next to the value. */
const UNIT_KEYS = ['HT', 'WT'];

/** Rendered at the canonical's precision; the stored value stays full precision. */
function display(value: number | null, precision?: number | null): string {
  if (value == null) return '';
  return precision == null ? String(value) : value.toFixed(precision);
}

/** Context entries synthesized from the measurement rows. */
export function contextFromMeasurements(
  measurements: Measurement[]
): Record<string, Context> {
  const byKey = new Map(
    measurements
      .filter((m) => m.canonicalMeasurementKey)
      .map((m) => [m.canonicalMeasurementKey as string, m])
  );

  const out: Record<string, Context> = {};
  Object.entries(MEASUREMENT_CONTEXT_KEYS).forEach(([contextClass, key]) => {
    const measurement = byKey.get(key);
    if (!measurement) return;
    out[contextClass] = {
      contextClass,
      value: display(measurement.value, measurement.precision),
      numericValue: measurement.value,
      unit: UNIT_KEYS.includes(key) ? measurement.unit : undefined,
      canonicalKey: key,
      canonicalMeasurement: measurement.canonicalMeasurement,
      editable: EDITABLE_KEYS.has(key),
    } as Context;
  });
  return out;
}

export const CONTEXT_GROUP_UUID = 'context-measurements';

/**
 * The Context-section measurements as one engine group. Never rendered by the
 * Measurements panel; appended to the worksheet groups for the row resolver
 * and the cascade only.
 */
export function contextMeasurementGroup(
  canonicals: CanonicalMeasurement[]
): MeasurementGroup {
  const labels: MeasurementLabel[] = canonicals
    .filter((c) => CONTEXT_OPERAND_KEYS.includes(c.key))
    .map((c, index) => ({
      uuid: `${CONTEXT_GROUP_UUID}-${c.key}`,
      createdAt: '',
      updatedAt: '',
      index,
      // Formula tooltips humanize operand keys to their label; the
      // abbreviation is what the Context section shows for these.
      label: c.abbreviation,
      canonicalMeasurement: c.uuid,
      canonicalMeasurementKey: c.key,
      precision: c.precision,
      unit: c.unit,
      tool: null,
      type: c.formula ? 'DERIVED' : 'MEASURED',
      formula: c.formula ?? null,
    }));
  return {
    uuid: CONTEXT_GROUP_UUID,
    createdAt: '',
    updatedAt: '',
    index: -1,
    label: 'Context',
    measurementLabels: labels,
  };
}
